import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useLocation, useNavigate, useParams } from 'react-router'
import { IconName } from '@fortawesome/fontawesome-svg-core'
import classNames from 'classnames'

// evertel
import {
    PageBody, Text, ContentPlaceholder,
    Nav, NavItem, NavLink, TabContent, TabPane,
    Icon, InfoBox, Row, Button, ButtonGroup, Breadcrumb, useUI,
    Tooltip,
    TooltipTrigger,
    TooltipContent
} from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { SettingsBar } from './elements/SettingsBar'
import { SchemaBuilderNoticeCard } from './elements/SchemaBuilderNoticeCard'
import { RoomTileSettingsModal } from './elements/RoomTileSettingsModal'
import { SchemaBuilderController } from '../../controller/schema-builder'
import { SchemaBuilderDnD } from '../schema-builder-dnd'
import { SchemaBuilderPreview } from './SchemaBuilderPreview'
import { normalize } from '@evertel/schema-parser'
import { SchemaMediaController } from '../../controller/medias/SchemaMediaController'
import { truncate } from '@evertel/utils'
import { SessionService } from '@evertel/session/SessionService'
import { SchemaProps } from '@evertel/types'
import { useUnsavedChangesGuard } from '../../utils'


interface SchemaBuilderProps {
    props?: any
}

const BUILDER_TABS = [
    { key: 'schema-builder', label: 'Form Builder', icon: 'keyboard' },
    { key: 'preview', label: 'Preview', icon: 'eye' }
] as { key: 'schema-builder' | 'preview', label: string, icon: IconName }[]

const SchemaBuilder: React.FC<SchemaBuilderProps> = observer(() => {

    const sessionService = useService(SessionService, [])
    const { addToast, isConfirmed } = useUI()

    const location = useLocation()
    const navigate = useNavigate()
    const { id, target } = useParams()

    const schemaId = Number(id)
    const departmentId = (location.state?.isAdmin || target === 'global') ? 0 : sessionService.selectedDepartmentId

    const schemaBuilderController = useService(SchemaBuilderController, [schemaId, departmentId])
    const schemaMediaController = useService(SchemaMediaController, [])

    const schemaKey = schemaBuilderController.schemaKey

    const [isLoading, setIsLoading] = useState(false)
    const [activeTab, setActiveTab] = useState<'schema-builder' | 'preview'>('schema-builder')
    const [showRoomTileModal, setShowRoomTileModal] = useState(false)

    // Prompts on ANY navigation away (links, back button, refresh) while there are unsaved changes
    const { allowNavigation } = useUnsavedChangesGuard(schemaBuilderController.hasUnsavedChanges)

    useEffect(() => {
        (async () => {
            setIsLoading(true)
            await schemaBuilderController.init(
                schemaId,
                departmentId
            )
            await schemaMediaController.init(
                schemaId
            )
            setIsLoading(false)
        })()
    }, [schemaBuilderController])

    let schema = {} as SchemaProps
    if (schemaBuilderController.schemaKey === 'root') {
        schema = (schemaBuilderController.schema?.layouts) ? normalize(schemaBuilderController.schema) : {} as SchemaProps
    } else {
        schema = normalize(schemaBuilderController.schema.internalSchemas[schemaBuilderController.schemaKey])
    }

    const gotoSchemaTab = (key) => {
        setActiveTab('schema-builder')
        schemaBuilderController.setSchemaKey(key)
    }

    const onDeleteSchema = async () => {
        const confirmed = await isConfirmed({
            title: 'Are you sure you want to delete this template?',
            message: 'If you delete this template, you won’t be able to restore it.',
            acceptButton: {
                label: 'Delete',
                color: 'danger'
            }
        })
        if (!confirmed) return

        try {
            await schemaBuilderController.deleteSchema()

            addToast({
                color: 'success',
                title: 'Template Deleted',
                message: 'Your template has been successfully deleted.'
            })

            // the template is gone, so whatever was unsaved no longer matters
            allowNavigation()
            navigate('/everdocs')
        } catch (error: any) {
            addToast({
                color: 'danger',
                title: 'Oops!',
                message: error.message
            })
        }
    }

    const isOrphaned = React.useMemo(() => {
        if (schemaKey === 'root') return false
        const rows = schemaBuilderController.schema?.layouts?.full ?? []
        return !rows.some((r: any) => {
            if (r?.type !== 'row' || !Array.isArray(r.items) || r.items.length !== 1) return false
            const it = r.items[0]
            // legacy variants we’ve seen elsewhere
            return it?.type === `&${schemaKey}` || it?.item?.type === `&${schemaKey}`
        })
    }, [schemaKey])

    // console.log('ID', schemaId)
    // console.log('NORMALIZED', toJS(schema))
    // console.log('RAW', toJS(controller.schema))

    const internalSchemaKeys = (schemaBuilderController.schema?.internalSchemas) ? Object.keys(schemaBuilderController.schema.internalSchemas) : []
    const isSchemaLoaded = !!schemaBuilderController.schema && !!schemaBuilderController.schema.layouts

    // where the breadcrumb's parent crumb points back to depends on how this screen was reached
    const parentCrumb = (() => {
        if (location.state?.from) {
            return { label: 'Back', onClick: () => navigate(location.state.from, { state: { isAdmin: location.state?.isAdmin } }) }
        }
        if (location.state?.isAdmin || target === 'global') {
            return { label: 'Evertel Catalog', onClick: () => navigate('/everdocs/schema/templates/global') }
        }
        if (!id) {
            return { label: 'New Template', onClick: () => navigate('/everdocs/schema/new') }
        }
        return { label: 'Templates', onClick: () => navigate('/everdocs/templates') }
    })()

    return (
        <PageBody
            flush
            className="schema-builder-page">
            <div className="p-4">
                <Breadcrumb
                    items={[
                        parentCrumb,
                        { label: 'Template builder', active: true }
                    ]}
                />
                <Text tag="h3">
                    Template builder
                </Text>
                <Text
                    tag="p"
                    color="muted">
                    Drag the menu items on the right to assemble the form template. You can then preview and configure it.
                </Text>
                <div className="schema-builder-card">
                    <SettingsBar
                        schemaBuilderController={schemaBuilderController}
                        allowNavigation={allowNavigation}
                    />
                    <div className="p-3">
                        {/* three tracks, the outer two forced equal, so the tabs sit dead centre
                            no matter how wide the notice next to them gets */}
                        <div className="schema-builder-tabs-bar my-4">
                            <ButtonGroup
                                className="schema-builder-tabs-pills m-0 p-2"
                                style={{
                                    backgroundColor: 'var(--bs-body-bg)',
                                    borderRadius: 999,
                                    height: 46
                                }}
                            >
                                {BUILDER_TABS.map(({ key, label, icon }) => (
                                    <Button
                                        key={key}
                                        className={classNames('d-flex align-items-center px-3', {
                                            'schema-builder-tab-active': activeTab === key
                                        })}
                                        color=""
                                        disabled={key === 'preview' && !isSchemaLoaded}
                                        onClick={() => setActiveTab(key)}
                                        style={{
                                            borderRadius: 999,
                                            border: 'none',
                                            backgroundColor: activeTab === key ? 'var(--connect-segmented-active-bg)' : 'transparent',
                                            fontSize: '0.85rem'
                                        }}>
                                        <Icon name={icon} className="me-2" />
                                        {label}
                                    </Button>
                                ))}
                            </ButtonGroup>
                            {(schemaBuilderController.hasUnsavedChanges) &&
                                <InfoBox
                                    color="warning"
                                    className="schema-builder-tabs-notice mb-0 py-2 px-3">
                                    <Text bold>
                                        <Icon
                                            name="triangle-exclamation"
                                            className="me-2"
                                        />
                                        You have unsaved changes.
                                    </Text>
                                    {/* only when the form itself changed: that is what makes the API snapshot
                                        a revision and pin every document already created from this template */}
                                    {(schemaBuilderController.hasFormChanges) &&
                                        <Text
                                            tag="p"
                                            className="mb-0 mt-1">
                                            Once saved, changes only apply to newly created documents.
                                        </Text>
                                    }
                                    {/* a field this edit deleted is still listed in the room tile: it has to be
                                        kept or dropped deliberately, so send the user to where that happens */}
                                    {(schemaBuilderController.hasTileFieldsRemovedThisSession) &&
                                        <Text
                                            tag="p"
                                            className="mb-0 mt-1">
                                            <Button
                                                color="transparent"
                                                className="p-0 text-decoration-underline"
                                                onClick={() => setShowRoomTileModal(true)}>
                                                Review room preview
                                            </Button>
                                            <Tooltip>
                                                <TooltipTrigger className="ms-1">
                                                    <Icon
                                                        name="circle-info"
                                                        color="warning"
                                                        aria-label="Why the room preview needs a review"
                                                    />
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    A field you removed from the form is still shown in the room preview tile.
                                                </TooltipContent>
                                            </Tooltip>
                                        </Text>
                                    }
                                </InfoBox>
                            }
                        </div>
                        {(activeTab === 'schema-builder' && !!internalSchemaKeys.length) &&
                            <Nav
                                variant="tabs"
                                role="tablist"
                                className="justify-content-center">
                                <NavItem style={{ display: 'flex' }} className="d-flex">
                                    <NavLink
                                        className="text-nowrap"
                                        active={activeTab === 'schema-builder'}
                                        onClick={() => { gotoSchemaTab('root') }}
                                    >
                                        <Icon
                                            name="table"
                                        />
                                        <Text
                                            className="ms-2 px-2 py-1"
                                            color={(schemaBuilderController.schemaKey === 'root') ? 'primary' : ''}
                                            bold={schemaBuilderController.schemaKey === 'root'}>
                                            Form Builder
                                        </Text>
                                    </NavLink>
                                    {internalSchemaKeys?.map((sKey, idx) => {
                                        const row = schemaBuilderController.schema.layouts.full.find((r: Record<string, any>) =>
                                            r.type === 'row'
                                            && (
                                                (r.items && r.items[0]?.type === 'list' && r.items[0]?.item?.type === `&${sKey}`)
                                                || (r.items && r.items[0]?.type === `&${sKey}`)
                                            )
                                        )
                                        const list = row?.items?.find((i: Record<string, any>) => i.type === `&${sKey}` || i.item?.type === `&${sKey}`)

                                        return (
                                            <NavLink
                                                key={idx}
                                                active={activeTab === 'schema-builder'}
                                                style={{ marginLeft: -10 }}
                                                onClick={() => { gotoSchemaTab(sKey) }}
                                                className="px-2 text-nowrap"
                                            >
                                                <Icon
                                                    name={(idx === 0) ? 'chevron-right' : 'pipe'}
                                                    size={10}
                                                />
                                                <Text
                                                    key={idx}
                                                    className="px-2 py-1 text-capitalize"
                                                    color={(schemaBuilderController.schemaKey === sKey) ? 'primary' : 'body-color'}
                                                    bold={schemaBuilderController.schemaKey === sKey}>
                                                    {truncate(list?.title || sKey, 15)}
                                                </Text>
                                            </NavLink>
                                        )
                                    })}
                                </NavItem>
                            </Nav>
                        }
                        <TabContent>
                            <TabPane
                                role="tabpanel"
                                visible={activeTab === 'schema-builder'}>
                                {(!departmentId) &&
                                    <SchemaBuilderNoticeCard
                                        className="mb-4 p-2"
                                        left={
                                            <div>
                                                <Icon
                                                    name="circle-info"
                                                    type="solid"
                                                    className="me-2"
                                                />
                                                <Text>
                                                    You are editing a <strong>Global Template.</strong>
                                                </Text>
                                            </div>
                                        }
                                    />
                                }
                                {!schema?.layouts?.full?.some((row: any) => row.items?.length) &&
                                    <Row
                                        align="center"
                                        className="mb-4">
                                        Add an empty row for each element you need to include, then drag the element to the empty row.
                                    </Row>
                                }
                                {isOrphaned && (
                                    <div className="p-3">
                                        <InfoBox color="warning" className="mb-3">
                                            This subform isn’t referenced in the root layout (orphaned).
                                        </InfoBox>
                                        <button
                                            className="btn btn-danger"
                                            onClick={() => {schemaBuilderController.removeInternalSchema(schemaKey); gotoSchemaTab('root')} }
                                        >
                                            Remove
                                        </button>
                                    </div>
                                )}
                                {(isLoading) ?
                                    <ContentPlaceholder />
                                    :
                                    <SchemaBuilderDnD
                                        schemaId={schemaId}
                                        schemaBuilderController={schemaBuilderController}
                                        schemaMediaController={schemaMediaController}
                                    />
                                }
                            </TabPane>
                            <TabPane
                                role="tabpanel"
                                visible={activeTab === 'preview'}>
                                {(!!schemaBuilderController.id) &&
                                    <SchemaBuilderNoticeCard
                                        className="mb-4"
                                        left={
                                            <div>
                                                <Icon
                                                    name="circle-info"
                                                    type="solid"
                                                    className="me-2"
                                                />
                                                <Text>
                                                    This template is used in <Text tag="span" bold>
                                                        {schemaBuilderController.documentsCount} {(schemaBuilderController.documentsCount === 1) ? 'document' : 'documents'}
                                                    </Text>.
                                                </Text>
                                            </div>
                                        }
                                        right={
                                            <div className="d-flex align-items-center gap-2 py-2">
                                                {/* a template can only be deleted while no documents depend on it */}
                                                {(!schemaBuilderController.documentsCount) &&
                                                    <Button
                                                        color="transparent"
                                                        onClick={onDeleteSchema}>
                                                        <Icon
                                                            name="trash"
                                                            color="danger"
                                                            className="me-2"
                                                        />
                                                        <Text color="danger">
                                                            Delete this template
                                                        </Text>
                                                    </Button>
                                                }
                                                <Button
                                                    color="neutral"
                                                    onClick={() => setShowRoomTileModal(true)}>
                                                    <Icon
                                                        name="comment-dots"
                                                        className="me-2"
                                                    />
                                                    Room preview settings
                                                </Button>
                                            </div>
                                        }
                                    />
                                }
                                {(schemaBuilderController.isInitialized && activeTab === 'preview') &&
                                    <SchemaBuilderPreview
                                        controller={schemaBuilderController}
                                    />
                                }
                            </TabPane>
                        </TabContent>
                    </div>
                </div>
            </div>
            {/* outside the tabs: the unsaved changes notice opens this too, and that notice
                shows on whichever tab the user is on */}
            <RoomTileSettingsModal
                visible={showRoomTileModal}
                onClose={() => setShowRoomTileModal(false)}
                schemaBuilderController={schemaBuilderController}
            />
        </PageBody>
    )
})

export {
    SchemaBuilder
}
